import { CompanyReportAccessData, ComplexValue, LoadOptions, Value } from 'app/types-shared';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { DefaultFilterValidation } from 'app/area/shared/default-filters/types-store';
import { AddFiltersHandler, DefaultFilterWire, DeleteAllFiltersHandler, DeleteFilterHandler,
  UpdateFilterHandler } from 'app/area/shared/default-filters/types';
import { AddSavedReportsHandler, LoadEvaluationHandler, OnUpdateSavedReport,
  UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';
import { SavedReportsValidation, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { CompanyReportAdminTranslation } from '../constants';
import { CompanyReportSectionType } from './metroline/types';
import {
  CompanyReportAdminFormState, CompanyReportItem, CompanyReportItemValidation, ExcelMappingValue,
  MetadataValidation, MetadataValue, SavedReportCompanyReport, TextsValidation, TextValue
} from './types-store';
import { InfoTextProps, ValidationProps } from 'ui';

export interface RootCode {
  rootCodes: Value[];
}

export interface FilterDomainWire {
  filterDomain: {
    domain: number;
    label: string;
    advancedSearchType: number;
    sequence: number;
  }[];
}

export interface FilterMetadataWire {
  domain: Value;
  advancedSearchType: number;
  codetable: number;
  categoryCodetable: number;
  searchRequired: boolean;
  isHierarchy: boolean;
}

export interface CompanyReportFilterWire {
  domain: Value;
  sequence: number;
  myValue: boolean;
  defaultValue: Value;
  defaultValue2: Value;
  periodPredef: Value;
}

export interface SavedReportCompanyReportWire {
  code: Value;
  details: SavedReportDetailsWire;
}

export interface SavedReportDetailsWire {
  reportId: SavedReportDetailsValueWire;
  reportName: SavedReportDetailsValueWire;
  reportResult: SavedReportDetailsValueWire;
}

export interface SavedReportDetailsValueWire {
  cell: string;
  range: string;
}

export interface EmailSettingItemWire {
  textSet: Value;
  text: Value;
}
export interface EmailSettingWire {
  report: boolean;
  body: EmailSettingItemWire;
}

export interface ExcelMappingsWire {
  [key: string]: ExcelMappingValueWire;
}

export interface ExcelMappingValueWire {
  cell: string;
  range: string;
}

export interface TextsWire {
  textSet: Value;
  text: Value;
  details: TextsDetailsValueWire;
}

export interface TextsDetailsValueWire {
  cell: string;
  range: string;
}

export interface MetadataWire {
  metadataType: Value;
  details: MetadataDetailsValueWire;
}

export interface MetadataDetailsValueWire {
  cell: string;
  range: string;
}

export interface PredefinedData {
  active: boolean;
  defaultDate: Value;
}

export interface CompanyReportItemWire {
  code?: Value;
  language: Value[];
  disabled: string;
  sequence: number;
  subset: Value[];
  predefinedDate: PredefinedData;
  template: Value;
  defaultTemplate: Value;
  emailSetting: EmailSettingWire;
  excelMapping: ExcelMappingsWire;
  savedReport: SavedReportCompanyReportWire[];
  filter: DefaultFilterWire[];
  text: TextsWire[];
  metadata: MetadataWire[];
}

export interface CompanyReportSectionProps {
  companyReportItem: CompanyReportItem;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  locale: string;
  validation: CompanyReportItemValidation;
  translation: CompanyReportAdminTranslation;
  section: CompanyReportSectionType;
  fetching?: boolean;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  updateItem: (item: Partial<CompanyReportItem>) => void;
  updateValidation: (validation: CompanyReportItemValidation) => void;
  resetValidation: (key: string) => void;
  updateTextDescription: (id: number, textSet: number) => void;

  updateTextItem: (textValue: TextValue) => void;
  updateTextItemDescription: (id: number, textId: number, textSet: number) => void;
  removeText: (id: number) => void;
  addText: () => void;

  resetTextValidation: (id: number, key?: keyof TextValue) => void;
  resetTextsValidation: (id: number) => void;

  removeAllTexts: () => void;
  textsValidation?: TextsValidation;

  updateMetadataItem: (metadataValue: MetadataValue) => void;
  removeMetadata: (id: number) => void;
  addMetadata: () => void;
  resetMetadataValidation: (id: number) => void;
  removeAllMetadata: () => void;
  metadataValidation?: MetadataValidation;
  companyReportAccess: CompanyReportAccessData;
  translationAdminAccess: boolean;
  displayError: (errorMessage: string) => void;
}

export type CompanyReportSection = CompanyReportSectionProps & WithMetroLineWrappedComponentProps<CompanyReportSectionType>;

export interface CompanyReportContainerProps {
  formState: CompanyReportAdminFormState;
  mainLanguage: number;
  shouldPrompt: boolean;
  validation: CompanyReportItemValidation;
  translation: CompanyReportAdminTranslation;
  companyReportAccess: CompanyReportAccessData;
  switchTranslationsToggle: (value: boolean) => void;
  saveCompanyReport: (item: CompanyReportItem) => void;
  deleteCompanyReport: () => void;
  updateValidation: (validation: CompanyReportItemValidation) => void;
  updateSavedReportValidation: (validation: SavedReportsValidation<SavedReportCompanyReport>) => void;
  updateFiltersValidation: (validation: { [domain: number]: DefaultFilterValidation }) => void;
  displayError: (errorMessage: string) => void;
  children?: React.ReactNode;
}

export interface CompanyReportFormProps {
  formState: CompanyReportAdminFormState;
  mainLanguage: number;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  shouldPrompt: boolean;
  section: CompanyReportSectionType;
  locale: string;
  translation: CompanyReportAdminTranslation;

  saveCompanyReport: (item: CompanyReportItem) => void;
  deleteCompanyReport: () => void;
  updateItem: (item: Partial<CompanyReportItem>) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
  updateTextDescription: (id: number, textSet: number) => void;
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  updateValidation: (validation: CompanyReportItemValidation) => void;
  resetValidation: (key: string) => void;
  displayError: (errorMessage: string) => void;

  switchDisplayDisabled: (displayDisabled: boolean) => void;
  selectCurrentSavedReports: (savedReport: SavedReportCompanyReport, onlyIdMode: boolean) => void;
  updateCurrentSavedReports: (savedReport: Partial<SavedReportCompanyReport>) => void;
  updateCurrentValidation: (validation: SavedReportValidation<SavedReportCompanyReport>) => void;

  loadEvaluation?: (favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportCompanyReport>) => void;

  addSavedReports: AddSavedReportsHandler<SavedReportCompanyReport>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportCompanyReport>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  updateSavedReportValidation: (validation: SavedReportsValidation<SavedReportCompanyReport>) => void;
  savedReportsValidation?: { [savedReportId: number]: SavedReportValidation<SavedReportCompanyReport> };
  loadSavedReportEvaluation?: LoadEvaluationHandler<SavedReportCompanyReport>;

  addFilters: AddFiltersHandler;
  updateFilter: UpdateFilterHandler;
  deleteFilter: DeleteFilterHandler;
  deleteAllFilters: DeleteAllFiltersHandler;
  updateFiltersValidation: (validation: { [domain: number]: DefaultFilterValidation }) => void;

  updateTextItem: (item: TextValue) => void;
  updateTextItemDescription: (id: number, textId: number, textSet: number) => void;
  removeText: (id: number) => void;
  addText: () => void;
  resetTextValidation: (id: number, key?: keyof TextValue) => void;
  resetTextsValidation: (id: number) => void;

  removeAllTexts: () => void;
  textsValidation?: TextsValidation;

  updateMetadataItem: (item: MetadataValue) => void;
  removeMetadata: (id: number) => void;
  addMetadata: () => void;
  resetMetadataValidation: (id: number) => void;
  removeAllMetadata: () => void;
  metadataValidation?: MetadataValidation;
  companyReportAccess: CompanyReportAccessData;
  translationAdminAccess: boolean;
  showTranslations: boolean;
  switchTranslationsToggle: (showTranslations: boolean) => void;
}

export interface CompanyReportLanguageProps extends WithMetroLineWrappedComponentProps<CompanyReportSectionType> {
  language: ComplexValue<number>[];
  mainLanguage: number;
  languageOptions: ComplexValue<number>[];
  showTranslations: boolean;
  validation: CompanyReportItemValidation;
  translation: CompanyReportAdminTranslation;
  section: string;
  switchTranslationsToggle: (value: boolean) => void;
  changeLanguage: (languageId: number, text: string) => void;
  copyLanguageToAll: (languageId: number, successMessage: string) => void;
}

export interface CompanyReportStickyHeaderProps {
  companyReportItem: CompanyReportItem;
  companyReportAccess: CompanyReportAccessData;
  fetchingTemplate: boolean;
  name: string;
  shouldPrompt: boolean;
  fetching: boolean;
  translation: CompanyReportAdminTranslation;
  displayError: (errorMessage: string) => void;
  onSave: () => void;
  onDelete: () => void;
}

export interface EmailSettingsProps extends CompanyReportSection {
  updateTextDescription: (id: number, textSet: number) => void;
}

export interface ExcelMappingsProps {
  companyReportItem: CompanyReportItem;
  validation: CompanyReportItemValidation;
  translation: CompanyReportAdminTranslation;
  updateItem: (item: Partial<CompanyReportItem>) => void;
}

export interface ExcelMappingsItemProps extends ExcelMappingsProps {
  value: ExcelMappingValue;
}

export interface ExcelMappingsTextProps extends ExcelMappingsProps {
  id: string;
  value: string;
  text: string;
  field: ExcelMappingsGroupField;
  maxLength: number;
  placeholder?: string;
  infoText?: InfoTextProps;
}

export interface TemplateMeatballProps {
  translation: CompanyReportAdminTranslation;
  uploadTemplate: () => void;
  downloadTemplate: () => void;
}

export interface TemplateItem {
  template: ComplexValue<number>;
}

export interface TextsProps {
  companyReportItem: CompanyReportItem;

  updateTextItem: (textValue: TextValue) => void;
  updateTextItemDescription: (id: number, textId: number, textSet: number) => void;
  removeText: (id: number) => void;
  removeAllTexts: () => void;
  addText: () => void;
  resetTextValidation: (id: number, key?: keyof TextValue) => void;
  resetTextsValidation: (id: number) => void;

  textsValidation?: TextsValidation;

  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  locale: string;
  validation: CompanyReportItemValidation;
  translation: CompanyReportAdminTranslation;
  translationAdminAccess: boolean;
}

export interface TextsItemProps extends TextsProps {
  value: TextValue;
}

export interface MetadataProps {
  companyReportItem: CompanyReportItem;

  updateMetadataItem: (metadataValue: MetadataValue) => void;
  removeMetadata: (id: number) => void;
  removeAllMetadata: () => void;
  addMetadata: () => void;
  resetMetadataValidation: (id: number, key?: keyof MetadataValue) => void;
  validation: CompanyReportItemValidation;

  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;
  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  locale: string;
  translation: CompanyReportAdminTranslation;
}

export interface MetadataItemProps extends MetadataProps {
  value: MetadataValue;
}

export enum ExcelMappingsGroupField {
  cell = 'cell',
  range = 'range'
}

export enum ExcelMappingParam {
  favouriteCondition = 'favouriteCondition',
  favouriteConditionValue = 'favouriteConditionValue',
  generationTime = 'generationTime',
  period = 'period',
  searchParameters = 'searchParameters',
  urlForWebApplication = 'urlForWebApplication'
}

export interface MetadataValidationError {
  id: number;
  field: keyof MetadataValue | string;
  error: ValidationProps;
}

export interface TextsValidationError {
  id: number;
  field: keyof TextValue | string;
  error: ValidationProps;
}
